import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";

const initialForm = {
  name: "",
  phone: "",
  email: "",
  quantity: "",
  message: "",
};

export default function InquiryModal({ product, open, onOpenChange }) {
  const [formData, setFormData] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setFormData(initialForm);
  }, [open]);

  const onChange = (event) => {
    setFormData((prev) => ({ ...prev, [event.target.name]: event.target.value }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!product?.id) return;

    try {
      setSubmitting(true);
      await apiClient.post("/inquiries", {
        product_id: product.id,
        product_name: product.name,
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        quantity: formData.quantity || null,
        message: formData.message || null,
      });

      toast.success("Inquiry sent successfully. We will call you shortly.");
      onOpenChange(false);
      setFormData(initialForm);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Unable to send inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="inquiry-modal" className="rounded-sm border-slate-200 sm:max-w-xl">
        <DialogHeader>
          <DialogTitle data-testid="inquiry-modal-title" className="font-heading text-2xl text-slate-900">
            Get Quote for {product?.name}
          </DialogTitle>
          <DialogDescription data-testid="inquiry-modal-description" className="text-slate-500">
            Share your requirement and HEXA INFOTECH team will contact you quickly.
          </DialogDescription>
        </DialogHeader>

        <form className="grid gap-4" onSubmit={onSubmit}>
          <Input
            data-testid="inquiry-name-input"
            name="name"
            required
            value={formData.name}
            onChange={onChange}
            placeholder="Your name"
            className="rounded-sm border-slate-300 focus-visible:ring-orange-500"
          />
          <Input
            data-testid="inquiry-phone-input"
            name="phone"
            required
            value={formData.phone}
            onChange={onChange}
            placeholder="Mobile number"
            className="rounded-sm border-slate-300 focus-visible:ring-orange-500"
          />
          <Input
            data-testid="inquiry-email-input"
            name="email"
            type="email"
            value={formData.email}
            onChange={onChange}
            placeholder="Email (optional)"
            className="rounded-sm border-slate-300 focus-visible:ring-orange-500"
          />
          <Input
            data-testid="inquiry-quantity-input"
            name="quantity"
            value={formData.quantity}
            onChange={onChange}
            placeholder="Quantity needed"
            className="rounded-sm border-slate-300 focus-visible:ring-orange-500"
          />
          <Textarea
            data-testid="inquiry-message-input"
            name="message"
            value={formData.message}
            onChange={onChange}
            placeholder="Tell us your requirement"
            className="min-h-24 rounded-sm border-slate-300 focus-visible:ring-orange-500"
          />

          <Button
            data-testid="inquiry-submit-button"
            type="submit"
            disabled={submitting}
            className="rounded-sm bg-orange-500 text-white hover:bg-orange-600"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Sending...
              </span>
            ) : (
              "Send Inquiry"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
